import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  SanitizeService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import { CopyIconSvg } from '@g99-product-web-ui/shared-util-lib';
@Component({
  selector: 'lib-input-textarea',
  templateUrl: './input-textarea.component.html',
  styleUrl: './input-textarea.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextareaComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InputTextareaComponent implements ControlValueAccessor {
  @Input() public placeholder = ''; // Placeholder for input
  @Input() public label = ''; // label for input
  @Input() public inputId = ''; // ID for the input field
  @Input() public name = ''; // Name for the input field
  @Input() public isInvalid = false; // Error state
  @Input() public errorMessage: { [key: string]: string } = {};
  @Input() public helperText = '';
  @Input() public formControls!: AbstractControl | null;
  @Input() public disabled = false;
  @Input() public showCopy = false;
  @ViewChild('textarea') textareaRef!: ElementRef;
  @Output() public inputChange = new EventEmitter<void>();
  public value = ''; // Value for FormControl
  public copyIcon = CopyIconSvg;

  constructor(
    private readonly _sanitizerService: SanitizeService,
    private readonly _toastService: ToastService
  ) {}

  public onChange: (value: string) => void = (value: string) => {
    // Invoke onChange callback if it has been registered
    if (this.onChange) {
      this.onChange(value);
    }
  };

  public onTouched: () => void = () => {
    // Invoke onTouched callback if it has been registered
    if (this.onTouched) {
      this.onTouched();
    }
  };

  // ControlValueAccessor implementation
  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onInputChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value); // Notify value change
    this.onTouched(); // Mark as touched
    this.inputChange.emit();
  }

  public getErrorMessage(): string | null {
    if (
      this.formControls &&
      this.formControls.invalid &&
      (this.formControls.dirty || this.formControls.touched)
    ) {
      const errors = this.formControls.errors;
      if (errors) {
        for (const errorKey in errors) {
          if (this.errorMessage[errorKey]) {
            return this.errorMessage[errorKey];
          }
        }
      }
    }
    return null;
  }

  public getCursorPosition(): number {
    const textarea = this.textareaRef.nativeElement as HTMLTextAreaElement;
    return textarea.selectionStart;
  }

  public get trustedCopyIcon() {
    return this._sanitizerService.sanitizeContent(this.copyIcon);
  }

  public copyToClipboard(): void {
    if (!this.value) {
      return;
    }
    navigator.clipboard
      .writeText(this.value)
      .then(() => {
        this._toastService.showSuccess('Copied!');
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
      });
  }
}
